/**
 * Panneau des reglages. Tout est persiste dans localStorage sous `jeu.reglages`
 * et annonce au jeu par `onReglage(cle, valeur)`.
 *
 * Le nom du fichier de journal de la session est affiche ici: c'est ce qu'il
 * faut regarder quand quelque chose se passe mal dans une partie.
 */
import { fichierSession, journal } from '../log/journal.ts'
import { audio } from '../audio/audio.ts'
import { Panneau, bouton, clicSonore, el, section } from './commun.ts'
import { ecrireLocal, lireLocal } from '../stockage.ts'

/** Clef de persistance des reglages. */
export const CLE_REGLAGES = 'jeu.reglages'

export type QualiteOmbres = 'haute' | 'moyenne' | 'aucune'

export interface Reglages {
  /** Son coupe. */
  muet: boolean
  /** Volume general, 0 a 1. */
  volume: number
  /** Afficher le compteur d'images par seconde. */
  fps: boolean
  /** Qualite des ombres portees. */
  ombres: QualiteOmbres
  /** Sensibilite du pivot de camera, 0.2 a 2. */
  sensibilite: number
}

const DEFAUT: Reglages = {
  muet: false,
  volume: 0.8,
  fps: false,
  ombres: 'haute',
  sensibilite: 1,
}

const OMBRES: readonly { id: QualiteOmbres; nom: string }[] = [
  { id: 'haute', nom: 'Haute' },
  { id: 'moyenne', nom: 'Moyenne' },
  { id: 'aucune', nom: 'Aucune' },
]

function borne(v: number, min: number, max: number, defaut: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : defaut
}

/** Lit les reglages persistes. Ne jette jamais: retombe sur les defauts. */
export function chargerReglages(): Reglages {
  try {
    const brut = lireLocal(CLE_REGLAGES)
    if (!brut) return { ...DEFAUT }
    const o = JSON.parse(brut) as Record<string, unknown>
    const ombres = o['ombres']
    return {
      muet: o['muet'] === true,
      volume: borne(o['volume'] as number, 0, 1, DEFAUT.volume),
      fps: o['fps'] === true,
      ombres: ombres === 'haute' || ombres === 'moyenne' || ombres === 'aucune' ? ombres : DEFAUT.ombres,
      sensibilite: borne(o['sensibilite'] as number, 0.2, 2, DEFAUT.sensibilite),
    }
  } catch {
    return { ...DEFAUT }
  }
}

function sauver(r: Reglages): void {
  try {
    ecrireLocal(CLE_REGLAGES, JSON.stringify(r))
  } catch (e) {
    journal.warn('reglages', 'impossible d ecrire les reglages', { erreur: String(e) })
  }
}

export class PanneauReglages extends Panneau {
  /** Un reglage a change. La cle est celle de `Reglages`, ou 'recentrer'. */
  onReglage: ((cle: string, valeur: unknown) => void) | null = null

  private reglages: Reglages
  private minuterieSauve: number | null = null

  constructor(parent: HTMLElement) {
    super(parent, 'reglages', 'Réglages', '⚙️', 'panneau-fond--reglages')
    this.reglages = chargerReglages()
  }

  /** Copie des reglages courants. */
  get valeurs(): Reglages {
    return { ...this.reglages }
  }

  ouvrir(): void {
    this.construire()
    this.afficher()
  }

  /**
   * Change un reglage. `silencieux` sert aux curseurs: on n'ecrit dans le
   * journal qu'a la fin du glissement, sinon il se remplirait de bruit.
   */
  private poser<K extends keyof Reglages>(cle: K, valeur: Reglages[K], silencieux = false): void {
    this.reglages = { ...this.reglages, [cle]: valeur } as Reglages
    this.planifierSauvegarde()
    if (!silencieux) journal.input('reglages', `${cle}=${String(valeur)}`)
    this.onReglage?.(cle, valeur)
  }

  /** Ecriture groupee dans localStorage: un glissement ne fait qu'un ecrit. */
  private planifierSauvegarde(): void {
    if (this.minuterieSauve !== null) window.clearTimeout(this.minuterieSauve)
    this.minuterieSauve = window.setTimeout(() => {
      this.minuterieSauve = null
      sauver(this.reglages)
    }, 200)
  }

  private sauverMaintenant(): void {
    if (this.minuterieSauve !== null) window.clearTimeout(this.minuterieSauve)
    this.minuterieSauve = null
    sauver(this.reglages)
  }

  private construire(): void {
    this.viderCorps()
    this.viderPied()

    // ---------------------------------------------------------------- son
    const sSon = section('Son', '🔊')
    const bMuet = el('button', 'bascule')
    bMuet.type = 'button'
    const majMuet = (): void => {
      bMuet.textContent = this.reglages.muet ? '🔇 Son coupé' : '🔊 Son actif'
      bMuet.classList.toggle('bascule--active', !this.reglages.muet)
      bMuet.setAttribute('aria-pressed', this.reglages.muet ? 'true' : 'false')
    }
    majMuet()
    bMuet.addEventListener('click', () => {
      const muet = !this.reglages.muet
      if (!muet) clicSonore()
      this.poser('muet', muet)
      audio.muet(muet)
      majMuet()
    })
    sSon.append(bMuet)
    sSon.append(
      this.curseur('volume', 'Volume', 0, 1, 0.05, (v) => `${Math.round(v * 100)} %`, (v) =>
        audio.volume(v),
      ),
    )
    this.corps.append(sSon)

    // ------------------------------------------------------------ affichage
    const sAff = section('Affichage', '🖥️')
    const bFps = el('button', 'bascule')
    bFps.type = 'button'
    const majFps = (): void => {
      bFps.textContent = this.reglages.fps ? '📈 Images par seconde affichées' : '📉 Images par seconde cachées'
      bFps.classList.toggle('bascule--active', this.reglages.fps)
      bFps.setAttribute('aria-pressed', this.reglages.fps ? 'true' : 'false')
    }
    majFps()
    bFps.addEventListener('click', () => {
      clicSonore()
      this.poser('fps', !this.reglages.fps)
      majFps()
    })
    sAff.append(bFps)

    sAff.append(el('div', 'reglage__nom', 'Qualité des ombres'))
    const segments = el('div', 'segments')
    for (const o of OMBRES) {
      const b = el('button', 'segment', o.nom)
      b.type = 'button'
      if (this.reglages.ombres === o.id) b.classList.add('segment--actif')
      b.addEventListener('click', () => {
        clicSonore()
        this.poser('ombres', o.id)
        for (const autre of Array.from(segments.children)) autre.classList.remove('segment--actif')
        b.classList.add('segment--actif')
      })
      segments.append(b)
    }
    sAff.append(segments)
    this.corps.append(sAff)

    // -------------------------------------------------------------- camera
    const sCam = section('Caméra', '🎥')
    sCam.append(this.curseur('sensibilite', 'Sensibilité du pivot', 0.2, 2, 0.05, (v) => `${v.toFixed(2)} ×`))
    sCam.append(
      bouton('🎯 Recentrer la caméra', () => {
        journal.input('reglages', 'recentrer la camera')
        this.onReglage?.('recentrer', true)
      }, 'bouton--sombre'),
    )
    this.corps.append(sCam)

    // -------------------------------------------------------------- session
    const sJournal = section('Journal de la partie', '📄')
    const nom = fichierSession()
    const boite = el('div', 'journal-fichier')
    boite.append(el('span', 'journal-fichier__etiquette', 'Fichier de session'))
    boite.append(el('code', 'journal-fichier__nom', nom ? `logs/${nom}` : 'session non ouverte'))
    sJournal.append(boite)
    sJournal.append(
      el('p', 'note', 'En cas de bogue, c’est ce fichier qu’il faut regarder: tout ce qui se passe y est écrit.'),
    )
    this.corps.append(sJournal)

    // ---- compte
    const sCompte = section('Compte', '👤')
    const nomCompte = lireLocal('jeu.compte')
    sCompte.append(
      el(
        'div',
        'reglages__note',
        nomCompte
          ? `Connecté comme ${nomCompte.charAt(0).toUpperCase()}${nomCompte.slice(1)}.`
          : 'Tu joues en invité sur cet appareil.',
      ),
    )
    sCompte.append(
      bouton(
        '🚪 Se déconnecter',
        () => {
          journal.input('reglages', 'deconnexion')
          this.onReglage?.('deconnexion', true)
        },
        'bouton--sombre',
      ),
    )
    this.corps.append(sCompte)

    this.pied.append(bouton('✔ Terminé', () => this.fermer(), 'bouton--vert'))
  }

  /**
   * Curseur relie a un reglage numerique. La valeur s'applique en direct, mais
   * n'est journalisee et ecrite qu'a la fin du glissement.
   */
  private curseur(
    cle: 'volume' | 'sensibilite',
    nom: string,
    min: number,
    max: number,
    pas: number,
    formater: (v: number) => string,
    aussi?: (v: number) => void,
  ): HTMLElement {
    const valeur = this.reglages[cle]
    const bloc = el('div', 'reglage')
    const haut = el('div', 'reglage__haut')
    haut.append(el('span', 'reglage__nom', nom))
    const lecture = el('span', 'reglage__valeur', formater(valeur))
    haut.append(lecture)
    bloc.append(haut)
    const entree = el('input', 'curseur')
    entree.type = 'range'
    entree.min = String(min)
    entree.max = String(max)
    entree.step = String(pas)
    entree.value = String(valeur)
    entree.setAttribute('aria-label', nom)
    entree.addEventListener('input', () => {
      const v = Number(entree.value)
      lecture.textContent = formater(v)
      this.poser(cle, v, true)
      aussi?.(v)
    })
    entree.addEventListener('change', () => {
      journal.input('reglages', `${cle}=${entree.value}`)
      this.sauverMaintenant()
    })
    bloc.append(entree)
    return bloc
  }

  protected override surFermeture(): void {
    this.sauverMaintenant()
  }

  override detruire(): void {
    this.sauverMaintenant()
    this.onReglage = null
    super.detruire()
  }
}
